import { createTRPCClient, httpBatchLink, TRPCClientError, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import superjson from 'superjson';
import type { AppRouter } from '@hp/api/router';
import { reportClientError } from '../lib/client-errors';
import { queryClient } from './queryClient';

const TRPC_URL = '/trpc';

/**
 * Cloud Run answers 414 to any URL whose path SEGMENT exceeds 100 characters, before the
 * request reaches the app — so nothing is logged and no alert can fire server-side. The
 * batch link joins every procedure of a batch into one segment (`/trpc/a.b,c.d,…`), which
 * a page firing five queries at once overran; the client then tried to superjson-parse the
 * platform's 414 body and surfaced "Unable to transform response from server".
 *
 * `methodOverride: 'POST'` moves query inputs into the request body, so the generated URL is
 * exactly `${TRPC_URL}/<comma-joined paths>?batch=1`. That makes `maxURLLength` an exact
 * bound on the segment: the batcher splits into several requests instead of building a URL
 * the platform will reject. (Long inputs never mattered — only the path segment does.)
 * `router.test.ts` in the API keeps every procedure path short enough to dispatch.
 */
const MAX_URL_PATH_SEGMENT = 100;
const BATCH_URL_LIMIT = TRPC_URL.length + 1 + MAX_URL_PATH_SEGMENT + '?batch=1'.length;

/**
 * From the moment the page starts going away, a failed request says nothing about the
 * server — the browser is cancelling work, not reporting a fault. `pagehide` covers every
 * teardown the browser performs on its own (the tab closed or discarded, the machine
 * suspending, a navigation away), and the redirect below sets the flag directly, because
 * navigating cancels every other request the page still had in flight. A page restored
 * from the back/forward cache is live again, so `pageshow` clears it.
 */
let pageIsLeaving = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageIsLeaving = true;
  });
  window.addEventListener('pageshow', () => {
    pageIsLeaving = false;
  });
}

/** Session gone → same behaviour as the REST client: back to login. */
const redirectOnUnauthorized: TRPCLink<AppRouter> =
  () =>
  ({ next, op }) =>
    observable((observer) =>
      next(op).subscribe({
        next: (value) => observer.next(value),
        complete: () => observer.complete(),
        error: (err) => {
          if (err.data?.code === 'UNAUTHORIZED') {
            pageIsLeaving = true;
            window.location.href = '/auth/login';
          }
          reportInfrastructureFailure(op.path, err);
          observer.error(err);
        },
      }),
    );

/**
 * A procedure failing for a reason the API did not name is an infrastructure fault, not a
 * refusal the user can act on: a platform error page the transformer cannot read, a 5xx, a
 * broken deploy. Those are reported to `/api/client-errors`, which logs and alerts them —
 * the page still shows its own message. Domain errors (a run already active, a missing
 * permission) carry a machine code and are expected, so they never alert.
 */
function reportInfrastructureFailure(path: string, err: TRPCClientError<AppRouter>): void {
  if (domainCodeOf(err) !== null) return;
  const code = err.data?.code;
  if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN') return;
  // A cancelled request (navigation, a superseded query) and an offline browser are not faults.
  if (err.cause?.name === 'AbortError' || err.name === 'AbortError') return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  if (pageIsLeaving || isTransportFailure(err)) return;
  reportClientError({ message: `tRPC ${path}: ${err.message}`, stack: err.stack, source: 'trpc' });
}

/**
 * A fetch that never delivered a response rejects with a TypeError — the Fetch spec
 * mandates that type for every network error, whatever the browser calls it ("Failed to
 * fetch" in Chrome, "NetworkError when attempting to fetch resource" in Firefox, "Load
 * failed" in Safari). That is what we get when the browser drops in-flight requests for
 * reasons that are not faults: a frozen tab being discarded, a laptop suspending, a
 * connection flapping, a redirect tearing the document down.
 *
 * Such a request never reaches the server, so no server-side log can corroborate it and
 * nobody can act on the alert. And a transport failure that IS our fault cannot arrive
 * here anyway: the report travels the same path as the request that failed, so the only
 * ones we ever receive are those where the path was working a moment later. Suppressing
 * them costs no real signal.
 *
 * Everything else still reports. A fault the server produced carries an HTTP status, and a
 * response that arrived but could not be parsed — a platform error page in place of the
 * procedure's result — rejects with a SyntaxError, not a TypeError.
 */
function isTransportFailure(err: TRPCClientError<AppRouter>): boolean {
  return err.cause?.name === 'TypeError';
}

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    redirectOnUnauthorized,
    httpBatchLink({
      url: TRPC_URL,
      transformer: superjson,
      methodOverride: 'POST',
      maxURLLength: BATCH_URL_LIMIT,
      fetch: (input, init) => fetch(input, { ...init, credentials: 'same-origin' }),
    }),
  ],
});

/**
 * `trpc.<module>.<procedure>.queryOptions(...)` / `.mutationOptions(...)` /
 * `.infiniteQueryOptions(...)` feed TanStack Query hooks; `.pathFilter()` /
 * `.queryKey()` target invalidation. Types come from the API's router.
 */
export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });

export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export function isTrpcClientError(err: unknown): err is TRPCClientError<AppRouter> {
  return err instanceof TRPCClientError;
}

/** Machine code of the domain error behind a failed procedure, e.g. 'SEGMENTATION_RUN_ACTIVE'. */
export function domainCodeOf(err: unknown): string | null {
  if (!isTrpcClientError(err)) return null;
  const data: unknown = err.data;
  if (data && typeof data === 'object' && 'domainCode' in data && typeof data.domainCode === 'string') {
    return data.domainCode;
  }
  return null;
}
